import React, { useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';
import { toast } from 'sonner';

import { Modal } from '@shared/components/bootstrap';
import { Retry } from '@shared/components/helpers';
import { Lightbox, Linkify } from '@shared/components/helpers';

import { ConfirmModal } from '@admin/components/helpers/confirm_modal';
import { Panel } from '@admin/components/helpers/panel';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { Spinner } from '@admin/components/spinner';
import { TicketMessagesPanel } from '@admin/components/ticket_messages/panel';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  useTicketQuery,
  TicketQuery,
  NoteNotableEnum,
  Status,
  useTicketClaimMutation,
  useTicketResolveMutation,
  useTicketReopenMutation,
  useTicketDeleteMutation,
  History__Subject,
} from '@admin/schema';
import { TicketState } from '@admin/types/ticket_state';
import { getPriorityText } from '@admin/utils/ticket_priorities';
import { getTicketableLink } from '@admin/utils/ticketable_links';

import { TicketForm } from './form';

export const TicketDetails: React.FC = () => {
  useTitle('View Ticket');
  const { ticketID } = useParams<{ ticketID: string }>();
  const [currentMode, setCurrentMode] = useState<'view' | 'edit'>('view');
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reopenDueDate, setReopenDueDate] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);

  const { data, loading, error, refetch } = useTicketQuery({
    client,
    variables: { id: ticketID },
  });

  // GraphQL mutation hooks
  const [ticketClaim] = useTicketClaimMutation({ client });
  const [ticketResolve] = useTicketResolveMutation({ client });
  const [ticketReopen] = useTicketReopenMutation({ client });
  const [ticketDelete] = useTicketDeleteMutation({ client });

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <Retry error={error} refetch={refetch} />;
  }

  if (!data?.ticket) {
    return <div className="alert alert-danger">Ticket not found</div>;
  }

  const ticket: TicketQuery['ticket'] = data.ticket;

  const handleEdit = () => {
    setCurrentMode('edit');
  };

  const handleCancelEdit = () => {
    setCurrentMode('view');
    refetch();
  };

  const handleClaim = async () => {
    try {
      const response = await ticketClaim({
        variables: { id: ticket.id },
      });

      if (response.data?.ticketClaim.status === Status.Ok) {
        refetch();
        toast.info('Ticket claimed successfully!');
      } else {
        toast.error(`Failed to claim ticket: ${response.data?.ticketClaim.error ?? 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('An unexpected error occurred while claiming the ticket. Please try again.');
    }
  };

  const handleResolve = async (resolutionType: 'done' | 'did_not_do') => {
    try {
      const response = await ticketResolve({
        variables: {
          id: ticket.id,
          resolutionType,
        },
      });

      if (response.data?.ticketResolve.status === Status.Ok) {
        setShowResolveModal(false);
        refetch();
        toast.info('Ticket resolved successfully!');
      } else {
        toast.error(`Failed to resolve ticket: ${response.data?.ticketResolve.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('An unexpected error occurred while resolving the ticket. Please try again.');
    }
  };

  const handleReopen = async () => {
    if (!reopenDueDate) {
      toast.error('Due date is required for reopening the ticket.');
      return;
    }

    try {
      // Convert datetime-local format to ISO8601 format
      const isoDate = new Date(reopenDueDate).toISOString();

      const response = await ticketReopen({
        variables: {
          id: ticket.id,
          dueDate: isoDate,
        },
      });

      if (response.data?.ticketReopen.status === Status.Ok) {
        setShowReopenModal(false);
        setReopenDueDate('');
        refetch();
        toast.info('Ticket reopened successfully!');
      } else {
        toast.error(`Failed to reopen ticket: ${response.data?.ticketReopen.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('An unexpected error occurred while reopening the ticket. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await ticketDelete({
        variables: { id: ticket.id },
      });

      if (response.data?.ticketDelete.status === Status.Ok) {
        setShowDeleteConfirm(false);
        toast.info('Ticket deleted successfully!');
        window.location.href = '/';
      } else {
        toast.error(`Failed to delete ticket: ${response.data?.ticketDelete.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('An unexpected error occurred while deleting the ticket. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
  };

  const getStatusText = () => {
    if (ticket.state === TicketState.Resolved && ticket.resolutionType === 'did_not_do') {
      return 'Closed - No Action Taken';
    }
    return capitalize(ticket.state) || 'Unknown';
  };

  const breadcrumb = (
    <h1 className="page-header text-overflow">
      <ol className="breadcrumb">
        <li>
          <NavLink to="/">Inbox</NavLink>
        </li>
        <li className="active">
          Task Ticket #{ticket.id} ({ticket.ticketableName || 'Unknown'})
        </li>
      </ol>
    </h1>
  );

  if (currentMode === 'edit') {
    return (
      <div>
        {breadcrumb}
        <TicketForm ticket={ticket} onClose={handleCancelEdit} />
      </div>
    );
  }

  return (
    <div>
      {breadcrumb}
      <Panel>
        <Panel.Header>
          <Panel.Title>Ticket Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p>
            <strong>Subject:</strong>{' '}
            {getTicketableLink({
              ticketableName: ticket.ticketableName,
              ticketableType: ticket.ticketableType,
              ticketableID: ticket.ticketableID,
            })}
          </p>
          <p>
            <strong>Issue Type:</strong>
            <span>
              {' '}
              {ticket.category} - {ticket.subCategory}
            </span>
          </p>
          <p>
            <strong>Status:</strong>
            <span> {getStatusText()}</span>
          </p>
          <p>
            <strong>Priority:</strong>
            <span> {getPriorityText(ticket.priority)}</span>
          </p>
          <p>
            <strong>Group:</strong>
            <span> {ticket.group?.name || 'Unassigned'}</span>
          </p>
          <p>
            <strong>Description:</strong>
          </p>
          <div className="pre-wrap" style={{ marginBottom: '15px' }}>
            <Linkify text={ticket.description || 'No description provided'} />
          </div>

          <p>
            <strong>Assignee:</strong>
            <span> {ticket.assignee?.name || 'Unassigned'}</span>
          </p>

          <p>
            <strong>Owner:</strong>
            <span> {ticket.owner?.name || 'Unknown'}</span>
          </p>

          <p>
            <strong>Due Date:</strong>
            <span> {formatDate(ticket.dueDate)}</span>
          </p>

          <p>
            <strong>Created:</strong>
            <span> {formatDate(ticket.createdAt)}</span>
          </p>

          <p>
            <strong>Updated:</strong>
            <span> {formatDate(ticket.updatedAt)}</span>
          </p>

          {ticket.state === TicketState.Resolved && (
            <p>
              <strong>Resolution:</strong>
              <span> {ticket.resolutionType === 'done' ? 'Done' : 'No Action Taken'}</span>
            </p>
          )}
        </Panel.Body>

        <Panel.Footer align="right">
          <button className="btn btn-primary" onClick={handleEdit}>
            Edit
          </button>

          {(ticket.state === TicketState.Pending || ticket.state === TicketState.Paused) && (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              Delete
            </button>
          )}

          {(ticket.state === TicketState.Open || ticket.state === TicketState.Paused) && (
            <button className="btn btn-success" onClick={() => setShowResolveModal(true)}>
              Resolve
            </button>
          )}

          {ticket.state === TicketState.Resolved && (
            <button className="btn btn-success" onClick={() => setShowReopenModal(true)}>
              Reopen
            </button>
          )}

          {!ticket.assignee && (
            <button className="btn btn-default" onClick={handleClaim}>
              Claim
            </button>
          )}
        </Panel.Footer>
      </Panel>

      {/* Images */}
      {ticket.images && ticket.images.length > 0 && (
        <Panel>
          <Panel.Header>
            <Panel.Title>Images ({ticket.images.length})</Panel.Title>
          </Panel.Header>
          <Panel.Body>
            <div className="row">
              {ticket.images.map((image) => (
                <div key={image.id} className="col-xs-6 col-sm-4 col-md-3 col-lg-2">
                  <div className="panel brick">
                    <a
                      href={image.pdf ? image.source : '#'}
                      target={image.pdf ? '_blank' : undefined}
                      rel={image.pdf ? 'noopener noreferrer' : undefined}
                      onClick={(event) => {
                        if (!image.pdf) {
                          event.preventDefault();
                          event.stopPropagation();
                          setSelectedImage(image.source);
                        }
                      }}
                    >
                      <figure className="brick-square">
                        <img
                          src={image.thumbnailURL}
                          width={480}
                          height={480}
                          className="brick-image"
                          alt={`Ticket ${image.pdf ? 'PDF' : 'image'} ${image.id}`}
                        />
                      </figure>
                      {image.pdf && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '5px',
                            right: '5px',
                            backgroundColor: 'rgba(217, 83, 79, 0.9)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                          }}
                        >
                          <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: '3px' }} />
                          PDF
                        </div>
                      )}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </Panel.Body>
        </Panel>
      )}

      {/* Messages */}
      <TicketMessagesPanel id={ticket.id} />

      {/* Notes */}
      <NotesPanel type={NoteNotableEnum.Ticket} id={ticket.id} />

      {/* History */}
      <HistoriesPanel type={History__Subject.Ticket} id={ticket.id} />

      {/* Modals */}
      {showResolveModal && (
        <Modal onClose={() => setShowResolveModal(false)}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Resolve Ticket?</Modal.Title>
              <Modal.Close close={() => setShowResolveModal(false)} />
            </Modal.Header>
            <Modal.Footer>
              <button className="btn btn-default" onClick={() => setShowResolveModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={() => handleResolve('did_not_do')}>
                Won't do it
              </button>
              <button className="btn btn-success" onClick={() => handleResolve('done')}>
                Did it
              </button>
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      )}

      {showReopenModal && (
        <Modal onClose={() => setShowReopenModal(false)}>
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>When will this ticket be due?</Modal.Title>
              <Modal.Close close={() => setShowReopenModal(false)} />
            </Modal.Header>
            <Modal.Body>
              <div className="form-group">
                <div className="col-sm-12">
                  <div className="form-inline">
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={reopenDueDate}
                      onChange={(e) => setReopenDueDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button className="btn btn-default" onClick={() => setShowReopenModal(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleReopen}>
                Save
              </button>
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          message="Are you sure you want to delete this ticket?"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Lightbox for image viewing */}
      {selectedImage && <Lightbox imageURL={selectedImage} onClose={() => setSelectedImage(undefined)} />}
    </div>
  );
};
